import React, { FC, useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  FormControl,
  FormHelperText,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { FileUploader } from "react-drag-drop-files";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import "./styles/updateJobDetail.css";
import "./styles/jobForm.css";
import AlertBannerContext from "../../Contexts/alertBannerContext";
import { AlertBannerCodeMap } from "../../Constants/AlertBannertCodeMap";
import {
  CurrencyCode,
  Job,
  JobPostingStatus,
  JobPreferredStartDate,
  UpdateApplicationVariable,
  UpdateJobFormData,
  UpdateJobResponse
} from "../../Graphql/types/job";
import {
  EmploymentDuration,
  EmploymentPosition,
  EmploymentPositionLevel,
  EmploymentType
} from "../../Graphql/types/employee";
import { RwandaLocationParser } from "../../LocationsParser/RwandaLocationParser";
import { updateJobQuery } from "../../Graphql/querries/jobQuery";
import { uploadFileGraphQL, fetchGraphQL } from "../../Graphql/utils";
import { JOBS } from "../Routes";

interface EditJobProps {
  jobDetail: Job;
  handleClose: () => void;
}

const EditJob: FC<EditJobProps> = (props) => {
  const { jobDetail, handleClose } = props;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [formData, setFormData] = useState<UpdateJobFormData | undefined>();
  const [districts, setDistrict] = useState<string[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const { alert, setAlert } = useContext(AlertBannerContext);
  const navigate = useNavigate();
  // Extract the 'id' from the URL parameters

  const {
    title,
    briefText,
    duration,
    preferredStartDate,
    totalPay,
    hiringManager,
    postingStatus,
    jobImage,
    jobLevel,
    jobLocation,
    jobRole,
    jobType
  } = jobDetail;

  /* eslint-disable no-useless-escape */
  const urlRegex =
    /https?:\/\/(www\.)?[a-zA-Z0-9-.\/]+(\.[a-zA-Z]{2,})+\/\S*\.(png|jpg)/;
  const FILE_SIZE = 1 * 1024 * 1024; // 2MB in bytes
  const SUPPORTED_FORMATS = ["DOC", "DOCX"]; // .docx];

  const formFileSupportedFormats = [
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/msword"
  ];

  const schema: yup.ObjectSchema<UpdateJobFormData> = yup.object().shape({
    title: yup
      .string()
      .required("Title is required")
      .min(2, "Title must be at least 2 chars")
      .matches(/^[A-Za-z\s'-]+$/, "Special chars are not allowed"),
    briefText: yup
      .string()
      .required("Brief text is required")
      .min(2, "Brief text must be at least 2 chars")
      .matches(/^[A-Za-z\s'-,]+$/, "Special chars are not allowed"),
    currencyCode: yup
      .string()
      .oneOf(Object.values(CurrencyCode), "Invalid Currency code selected")
      .required("Currency code is required")
      .min(2, "Currency code must be at least 2 chars")
      .matches(/^[A-Za-z\s'-_]+$/, "Special chars are not allowed"),
    totalPay: yup
      .number()
      .required("Total Pay is required")
      .min(2, "Total Pay must be at least 2 digits"),
    hiringManager: yup
      .string()
      .required("Hiring Manager is required")
      .min(2, "Hiring Manager must be at least 2 digits")
      .matches(/^[A-Za-z\s'-]+$/, "Special chars are not allowed"),
    jobImage: yup
      .string()
      .required("Hiring Manager is required")
      .min(2, "Hiring Manager must be at least 2 digits")
      .matches(urlRegex, "Linkedin url must be a valid URL"),
    postingStatus: yup
      .string()
      .oneOf(Object.values(JobPostingStatus), "Invalid Job Posting selected")
      .required("Job Posting is required")
      .min(2, "Job Posting must be at least 2 chars")
      .matches(/^[A-Za-z\s'-_]+$/, "Special chars are not allowed"),
    jobRole: yup
      .string()
      .oneOf(Object.values(EmploymentPosition), "Invalid Job Role selected")
      .required("Job Role is required")
      .min(2, "Job Role must be at least 2 chars")
      .matches(/^[A-Za-z\s'-_]+$/, "Special chars are not allowed"),
    jobLevel: yup
      .string()
      .oneOf(
        Object.values(EmploymentPositionLevel),
        "Invalid Job Position Level selected"
      )
      .required("Job Position Level is required")
      .min(2, "Job Position Level must be at least 2 chars")
      .matches(/^[A-Za-z\s'-_]+$/, "Special chars are not allowed"),
    jobType: yup
      .string()
      .oneOf(Object.values(EmploymentType), "Invalid Job Type selected")
      .required("Job Type is required")
      .min(2, "Job Type must be at least 2 chars")
      .matches(/^[A-Za-z\s'-_]+$/, "Special chars are not allowed"),
    duration: yup
      .string()
      .oneOf(Object.values(EmploymentDuration), "Invalid Duration selected")
      .required("Duration is required")
      .min(2, "Duration must be at least 2 chars")
      .matches(/^[A-Za-z\s'-_]+$/, "Special chars are not allowed"),
    preferredStartDate: yup
      .string()
      .oneOf(
        Object.values(JobPreferredStartDate),
        "Invalid Start Date selected"
      )
      .required("Start Date is required")
      .min(2, "Start Date must be at least 2 chars")
      .matches(/^[A-Za-z\s'-_]+$/, "Special chars are not allowed"),
    jobLocation: yup
      .string()
      .oneOf(Object.values(districts), "Invalid Location selected")
      .required("Location is required")
      .min(2, "Location must be at least 2 chars")
      .matches(/^[A-Za-z\s'-_]+$/, "Special chars are not allowed"),
    description: yup.lazy((fileItem) => {
      if (fileItem) {
        return yup
          .mixed<File>()
          .required("Please attach job description !")
          .test(
            "fileSize",
            "File size is too large (max 2MB)",
            (value) => !value || (value && value.size <= FILE_SIZE)
          )
          .test(
            "fileType",
            "Unsupported file format. Only word doc and PDF are allowed",
            (value) =>
              !value || (value && formFileSupportedFormats.includes(value.type))
          );
      }

      return yup.mixed<File>().optional();
    })
  });

  const {
    setValue,
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<UpdateJobFormData>({
    resolver: yupResolver(schema)
  });

  useEffect(() => {
    (async () => {
      const rwandaLocationParser = new RwandaLocationParser();
      await rwandaLocationParser.getLocationData();
      setDistrict(rwandaLocationParser.getAllDistricts());
    })();
  }, []);

  const handleUploadFile = (fileInput: File) => {
    setFile(fileInput);
    setValue("description", fileInput, { shouldValidate: true }); // Update form state
  };

  const { mutate, isPending } = useMutation<
    UpdateJobResponse,
    Error,
    UpdateApplicationVariable
  >({
    mutationFn: (data) =>
      file
        ? uploadFileGraphQL(updateJobQuery, { input: data }, file, setAlert)
        : fetchGraphQL(updateJobQuery, { input: data }, setAlert),
    mutationKey: ["updateJob"],
    onSuccess: (response) => {
      navigate(`${JOBS}/${response.updateJob.id}`);
      handleClose();
    }
  });

  const handleClickSubmit = (formInput: UpdateJobFormData) => {
    // omitting description to be sent through the api
    const { description, ...jobFormData } = formInput;
    setFormData({ ...formInput, description });
    mutate({ ...jobFormData, id: jobDetail.id });
  };

  return (
    <Container
      maxWidth="md"
      className="jobFormContainer"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        flexDirection: "column",
        boxSizing: "border-box",
        padding: "0 2em",
        maxWidth: "100%",
        maxHeight: "85vh"
      }}
    >
      {isPending && (
        <Stack width="100%" paddingBottom="1em" alignSelf="center">
          <LinearProgress />
        </Stack>
      )}
      <Box
        component="form"
        noValidate
        autoComplete="on"
        style={{ width: "100%" }}
        display="flex"
        flexDirection="column"
        gap={1.7}
        onSubmit={handleSubmit(handleClickSubmit)}
      >
        <>
          <Stack>
            <Stack>
              <Controller
                name="title"
                control={control}
                defaultValue={title}
                render={({ field }) => (
                  <TextField
                    {...field}
                    error={!!errors.title}
                    size="medium"
                    id="Job Title"
                    label="Enter job Title"
                    variant="outlined"
                    helperText={errors.title?.message}
                    type="text"
                    fullWidth
                    margin="normal"
                    style={{ width: "100%!important" }}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                )}
              />
              <Controller
                name="briefText"
                control={control}
                defaultValue={briefText}
                render={({ field }) => (
                  <TextField
                    {...field}
                    error={!!errors.briefText}
                    size="medium"
                    id="Job briefText"
                    label="Enter job briefText"
                    variant="outlined"
                    helperText={errors.briefText?.message}
                    type="text"
                    fullWidth
                    margin="normal"
                    style={{ width: "100%!important" }}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                )}
              />
              <Controller
                name="currencyCode"
                control={control}
                defaultValue={CurrencyCode.RWF}
                render={({ field }) => (
                  <>
                    <FormControl
                      fullWidth
                      style={{ margin: "1em auto 0.7em auto" }}
                    >
                      <InputLabel shrink id="currency-code-select-label">
                        Select currency code
                      </InputLabel>
                      <Select
                        {...field}
                        labelId="currency-code-select-label"
                        error={!!errors.currencyCode}
                        id="currency-code-select"
                        name="degree"
                        label="Select currency code"
                        size="medium"
                        fullWidth
                        style={{ width: "100%!important" }}
                      >
                        {Object.values(CurrencyCode).map((currencyCode) => (
                          <MenuItem key={currencyCode} value={currencyCode}>
                            {currencyCode}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {!!errors.currencyCode && (
                      <FormHelperText
                        sx={{ color: "rgb(211, 47, 47)", pl: "1em" }}
                      >
                        {errors.currencyCode.message}
                      </FormHelperText>
                    )}
                  </>
                )}
              />
              <Controller
                name="totalPay"
                control={control}
                defaultValue={totalPay}
                render={({ field }) => (
                  <TextField
                    {...field}
                    error={!!errors.totalPay}
                    size="medium"
                    id="Job totalPay"
                    label="Enter job totalPay"
                    variant="outlined"
                    helperText={errors.totalPay?.message}
                    type="text"
                    fullWidth
                    margin="normal"
                    style={{ width: "100%!important" }}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                )}
              />
              <Controller
                name="hiringManager"
                control={control}
                defaultValue={hiringManager}
                render={({ field }) => (
                  <TextField
                    {...field}
                    error={!!errors.hiringManager}
                    size="medium"
                    id="Job Title"
                    label="Enter hiringManager"
                    variant="outlined"
                    helperText={errors.hiringManager?.message}
                    type="text"
                    fullWidth
                    margin="normal"
                    style={{ width: "100%!important" }}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                )}
              />
              <Controller
                name="jobImage"
                control={control}
                defaultValue={jobImage}
                render={({ field }) => (
                  <TextField
                    {...field}
                    error={!!errors.jobImage}
                    size="medium"
                    id="Job image"
                    label="Enter job image link (png/jpg)"
                    variant="outlined"
                    helperText={errors.jobImage?.message}
                    type="text"
                    fullWidth
                    margin="normal"
                    style={{ width: "100%!important" }}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                )}
              />
              <Controller
                name="postingStatus"
                control={control}
                defaultValue={postingStatus}
                render={({ field }) => (
                  <>
                    <FormControl
                      fullWidth
                      style={{ margin: "1em auto 0.7em auto" }}
                    >
                      <InputLabel shrink id="posting-status-select-label">
                        Select Job Posting Status
                      </InputLabel>
                      <Select
                        {...field}
                        labelId="posting-status-select-label"
                        error={!!errors.postingStatus}
                        id="posting-tatus-select"
                        name="postingStatus"
                        label="Select Job Posting Status"
                        size="medium"
                        fullWidth
                        style={{ width: "100%!important" }}
                      >
                        {Object.values(JobPostingStatus).map((jobStatus) => (
                          <MenuItem key={jobStatus} value={jobStatus}>
                            {jobStatus}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {!!errors.postingStatus && (
                      <FormHelperText
                        sx={{ color: "rgb(211, 47, 47)", pl: "1em" }}
                      >
                        {errors.postingStatus.message}
                      </FormHelperText>
                    )}
                  </>
                )}
              />
              <Controller
                name="jobRole"
                control={control}
                defaultValue={jobRole}
                render={({ field }) => (
                  <>
                    <FormControl
                      fullWidth
                      style={{ margin: "1em auto 0.7em auto" }}
                    >
                      <InputLabel shrink id="jobRole-select-label">
                        Select Job Role (Employment Position)
                      </InputLabel>
                      <Select
                        {...field}
                        labelId="jobRole-select-label"
                        error={!!errors.jobRole}
                        id="jobRole-select"
                        name="jobRole"
                        label="Select Job Role (Employment Position)"
                        size="medium"
                        fullWidth
                        style={{ width: "100%!important" }}
                      >
                        {Object.values(EmploymentPosition).map((position) => (
                          <MenuItem key={position} value={position}>
                            {position}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {!!errors.jobRole && (
                      <FormHelperText
                        sx={{ color: "rgb(211, 47, 47)", pl: "1em" }}
                      >
                        {errors.jobRole.message}
                      </FormHelperText>
                    )}
                  </>
                )}
              />

              <Controller
                name="jobLevel"
                control={control}
                defaultValue={jobLevel}
                render={({ field }) => (
                  <>
                    <FormControl
                      fullWidth
                      style={{ margin: "1em auto 0.7em auto" }}
                    >
                      <InputLabel shrink id="jobLevel-select-label">
                        Select Job Level (Employment Position Level)
                      </InputLabel>
                      <Select
                        {...field}
                        labelId="jobLevel-select-label"
                        error={!!errors.jobLevel}
                        id="jobLevel-select"
                        name="jobLevel"
                        label="Select Job Level (Employment Position Level)"
                        size="medium"
                        fullWidth
                        style={{ width: "100%!important" }}
                      >
                        {Object.values(EmploymentPositionLevel).map(
                          (positionLevel) => (
                            <MenuItem key={positionLevel} value={positionLevel}>
                              {positionLevel}
                            </MenuItem>
                          )
                        )}
                      </Select>
                    </FormControl>
                    {!!errors.jobLevel && (
                      <FormHelperText
                        sx={{ color: "rgb(211, 47, 47)", pl: "1em" }}
                      >
                        {errors.jobLevel.message}
                      </FormHelperText>
                    )}
                  </>
                )}
              />
              <Controller
                name="jobType"
                control={control}
                defaultValue={jobType}
                render={({ field }) => (
                  <>
                    <FormControl
                      fullWidth
                      style={{ margin: "1em auto 0.7em auto" }}
                    >
                      <InputLabel shrink id="jobType-select-label">
                        Select Job Type (Employment Type)
                      </InputLabel>
                      <Select
                        {...field}
                        labelId="jobType-select-label"
                        error={!!errors.jobType}
                        id="jobType-select"
                        name="jobType"
                        label="Select Job Type (Employment Type)"
                        size="medium"
                        fullWidth
                        style={{ width: "100%!important" }}
                      >
                        {Object.values(EmploymentType).map((jobTypeItem) => (
                          <MenuItem key={jobTypeItem} value={jobTypeItem}>
                            {jobTypeItem}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {!!errors.jobType && (
                      <FormHelperText
                        sx={{ color: "rgb(211, 47, 47)", pl: "1em" }}
                      >
                        {errors.jobType.message}
                      </FormHelperText>
                    )}
                  </>
                )}
              />
              <Controller
                name="duration"
                control={control}
                defaultValue={duration}
                render={({ field }) => (
                  <>
                    <FormControl
                      fullWidth
                      style={{ margin: "1em auto 0.7em auto" }}
                    >
                      <InputLabel shrink id="duration-select-label">
                        Select Job Duration
                      </InputLabel>
                      <Select
                        {...field}
                        labelId="duration-select-label"
                        error={!!errors.duration}
                        id="duration-select"
                        name="duration"
                        label="Select Job Duration"
                        size="medium"
                        fullWidth
                        style={{ width: "100%!important" }}
                      >
                        {Object.values(EmploymentDuration).map(
                          (durationItem) => (
                            <MenuItem key={durationItem} value={durationItem}>
                              {durationItem}
                            </MenuItem>
                          )
                        )}
                      </Select>
                    </FormControl>
                    {!!errors.duration && (
                      <FormHelperText
                        sx={{ color: "rgb(211, 47, 47)", pl: "1em" }}
                      >
                        {errors.duration.message}
                      </FormHelperText>
                    )}
                  </>
                )}
              />
              <Controller
                name="preferredStartDate"
                control={control}
                defaultValue={preferredStartDate}
                render={({ field }) => (
                  <>
                    <FormControl
                      fullWidth
                      style={{ margin: "1em auto 0.7em auto" }}
                    >
                      <InputLabel shrink id="preferredStartDate-select-label">
                        Select Job start date
                      </InputLabel>
                      <Select
                        {...field}
                        labelId="preferredStartDate-select-label"
                        error={!!errors.preferredStartDate}
                        id="preferredStartDate-select"
                        name="preferredStartDate"
                        label="Select Job start date"
                        size="medium"
                        fullWidth
                        style={{ width: "100%!important" }}
                      >
                        {Object.values(JobPreferredStartDate).map(
                          (startDate) => (
                            <MenuItem key={startDate} value={startDate}>
                              {startDate}
                            </MenuItem>
                          )
                        )}
                      </Select>
                    </FormControl>
                    {!!errors.preferredStartDate && (
                      <FormHelperText
                        sx={{ color: "rgb(211, 47, 47)", pl: "1em" }}
                      >
                        {errors.preferredStartDate.message}
                      </FormHelperText>
                    )}
                  </>
                )}
              />
              <Controller
                name="jobLocation"
                control={control}
                defaultValue={jobLocation}
                render={({ field }) => (
                  <>
                    <FormControl
                      fullWidth
                      style={{ margin: "1em auto 0.7em auto" }}
                    >
                      <InputLabel shrink id="jobLocation-select-label">
                        Select Job location
                      </InputLabel>
                      <Select
                        {...field}
                        labelId="jobLocation-select-label"
                        error={!!errors.jobLocation}
                        id="jobLocation-select"
                        name="jobLocation"
                        label="Select Job location"
                        size="medium"
                        fullWidth
                        style={{ width: "100%!important" }}
                      >
                        {Object.values(districts).map((district) => (
                          <MenuItem key={district} value={district}>
                            {district}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {!!errors.jobLocation && (
                      <FormHelperText
                        sx={{ color: "rgb(211, 47, 47)", pl: "1em" }}
                      >
                        {errors.jobLocation.message}
                      </FormHelperText>
                    )}
                  </>
                )}
              />
            </Stack>
          </Stack>
          <Stack>
            <Typography variant="h6">Job Description (Optional)</Typography>
            <Stack>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <Container
                    style={{
                      width: "100%!important",
                      display: "flex",
                      justifyContent: "center",
                      paddingTop: "1em",
                      flexDirection: "column",
                      gap: 1
                    }}
                  >
                    <FileUploader
                      handleChange={(fileData) => {
                        handleUploadFile(fileData);
                        field.onChange(fileData);
                      }}
                      types={SUPPORTED_FORMATS}
                      maxSize={FILE_SIZE}
                      label={
                        errors.description?.message ||
                        "Upload or drop your job description here"
                      }
                      uploadedLabel="Resume uploaded successfully"
                      hoverTiltle="Drop your job description here"
                      required
                      disabled={false}
                      classes="fileUploader"
                      onTypeError={(error) => {
                        /* eslint-disable no-console */
                        console.log("Used wrong type", error);
                      }}
                      onSizeError={(fileItem: File) => {
                        /* eslint-disable no-console */
                        console.log("File fize is limited", fileItem.size);
                      }}
                    />
                    {!!file && (
                      <Typography
                        variant="body2"
                        style={{
                          width: "100%",
                          textAlign: "center",
                          wordWrap: "break-word",
                          padding: "1.2em 0 0 0"
                        }}
                      >
                        <b>Uploaded: </b>
                        {`${file.name}`}
                      </Typography>
                    )}
                    {!!errors.description?.message && (
                      <Typography
                        variant="body2"
                        width="100%"
                        textAlign="center"
                        color="rgb(211, 47, 47)"
                        padding="1.2em 0 0 0"
                      >
                        {errors.description?.message}
                      </Typography>
                    )}
                    {!!alert && (
                      <Typography
                        variant="body2"
                        width="100%"
                        textAlign="center"
                        color="rgb(211, 47, 47)"
                        padding="1.2em 0 0 0"
                      >
                        {AlertBannerCodeMap[alert.code] ||
                          AlertBannerCodeMap.INTERNAL_SERVER_ERROR}
                      </Typography>
                    )}
                  </Container>
                )}
              />
            </Stack>
          </Stack>
          <Stack>
            <Button
              size="large"
              type="submit"
              variant="contained"
              fullWidth
              sx={{
                mt: "2em",
                backgroundColor: "rgb(34, 47, 62)", // Custom background color
                "&:hover": {
                  backgroundColor: "rgb(34 47 62 / 87%)" // Hover background color
                }
              }}
            >
              Update Job
            </Button>
          </Stack>
        </>
      </Box>
    </Container>
  );
};

export default EditJob;
